import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Icon, Input, Textarea } from "@legal-500/legal500-design-system";
import { useEffect, useState } from "react";
import { Controller, useForm, type Resolver } from "react-hook-form";
import type { PackageWithFeatureCodes } from "../../api/packages.service";
import type { AsyncSelectOption } from "../Form/AsyncSearchableSelect/AsyncSearchableSelect";
import { AsyncSearchableSelect } from "../Form/AsyncSearchableSelect/AsyncSearchableSelect";
import { useSubscriptionLocationOptions } from "../../pages/Subscriptions/hooks";
import { withGlobalRegionFirst } from "../../hooks/useJurisdictionOptions";
import {
  HISTORICAL_DATA_FEATURE,
  HISTORICAL_DATA_GLOBAL_HINT,
} from "../../constants/historicalData";
import {
  PackageFormSchema,
  type PackageFormData,
  PackageType,
  type PackageTypeValue,
} from "../../schemas/packageSchema";
import { FeaturePicker, type FeaturePickerColumns } from "../FeaturePicker";
import { MetadataQuantitiesConfig } from "../MetadataQuantitiesConfig";
import styles from "./PackageForm.module.css";

type LocationType = "region" | "country";

export type PackageFormProps = {
  initialValues?: Partial<PackageWithFeatureCodes>;
  onSubmit: (values: PackageFormData) => void;
  onCancel: () => void;
  submitLabel: string;
  isSubmitting?: boolean;
  mode: "create" | "edit";
  onDirtyChange?: (isDirty: boolean) => void;
  featurePickerColumns?: FeaturePickerColumns;
  featurePickerCompact?: boolean;
  featurePickerAutoFit?: boolean;
};

export function PackageForm({
  initialValues,
  onSubmit,
  onCancel,
  submitLabel,
  isSubmitting = false,
  mode,
  onDirtyChange,
  featurePickerColumns,
  featurePickerCompact,
  featurePickerAutoFit,
}: PackageFormProps) {
  // Determine package type from API response or default to PRODUCT for create mode
  const detectedType: PackageTypeValue =
    mode === "edit" && initialValues?.type
      ? initialValues.type === "default"
        ? PackageType.DEFAULT
        : PackageType.PRODUCT
      : PackageType.PRODUCT;

  const [packageType, setPackageType] = useState<PackageTypeValue>(detectedType);
  const [locationType, setLocationType] = useState<LocationType>("country");

  const {
    regionOptions: locationRegionOptions,
    countryOptions: locationCountryOptions,
    detectLocationType,
  } = useSubscriptionLocationOptions();

  const {
    register,
    handleSubmit,
    control,
    watch,
    formState: { errors, isDirty },
    setValue,
    getValues,
  } = useForm<PackageFormData>({
    resolver: zodResolver(PackageFormSchema) as Resolver<PackageFormData>,
    defaultValues: {
      type: detectedType,
      name: initialValues?.name || "",
      description: initialValues?.description || "",
      productCode: initialValues?.productCode || "",
      featureCodes: initialValues?.featureCodes || [],
      metadataQuantities: initialValues?.metadataQuantities || null,
      isActive: initialValues?.isActive ?? true,
      defaultSiteStructureId: initialValues?.defaultSiteStructureId ?? null,
    },
  });

  const isActive = watch("isActive");
  const featureCodes = watch("featureCodes");
  const includesHistoricalData = (featureCodes ?? []).includes(HISTORICAL_DATA_FEATURE);

  useEffect(() => {
    if (includesHistoricalData && !getValues("defaultSiteStructureId")) {
      setLocationType("region");
    }
  }, [includesHistoricalData, getValues]);

  useEffect(() => {
    setValue("type", packageType);
  }, [packageType, setValue]);

  useEffect(() => {
    if (initialValues?.defaultSiteStructureId && locationRegionOptions.length > 0) {
      setLocationType(detectLocationType(String(initialValues.defaultSiteStructureId)));
    }
  }, [initialValues?.defaultSiteStructureId, locationRegionOptions.length, detectLocationType]);

  useEffect(() => {
    if (onDirtyChange) {
      onDirtyChange(isDirty);
    }
  }, [isDirty, onDirtyChange]);

  function handlePackageTypeChange(newType: PackageTypeValue) {
    setPackageType(newType);
    setValue("type", newType);
  }

  const isDefaultPackage = packageType === PackageType.DEFAULT;
  const isProductPackage = packageType === PackageType.PRODUCT;
  const ELITE_FEATURES = ["city_elite", "global_elite"];
  const hasEliteFeatures = featureCodes?.some((code: string) => ELITE_FEATURES.includes(code));

  const [submitAttemptCount, setSubmitAttemptCount] = useState(0);
  const [missingFieldsCount, setMissingFieldsCount] = useState(0);

  // Watch for feature changes and reset state
  const watchedFeatureCodes = watch("featureCodes");
  useEffect(() => {
    setSubmitAttemptCount(0);
  }, [watchedFeatureCodes]);

  const onFormSubmit = async (data: PackageFormData) => {
    // First submit attempt - increment counter to show warnings if fields missing
    if (submitAttemptCount === 0) {
      setSubmitAttemptCount(1);

      // Only block if there are missing fields
      if (missingFieldsCount > 0) {
        return; // Block and show warning
      }

      // No missing fields, proceed immediately
    }

    // Second submit attempt or no missing fields - proceed
    await onSubmit(data);
  };

  const handleSaveAnyway = () => {
    // Submit the form (this will be the second attempt)
    handleSubmit(onFormSubmit)();
  };

  return (
    <form onSubmit={handleSubmit(onFormSubmit)} className={styles.form}>
      {/* 1. Package Type + Status Grid */}
      <div className={styles.typeStatusGrid}>
        {/* 1a. Package Type Section */}
        <section className={styles.formSection}>
          <h3 className={styles.sectionTitle}>Package Type</h3>
          <div className={styles.formGroup}>
            <div className={styles.radioGroup} role="radiogroup" aria-label="Package type selector">
              <label className={styles.radioLabel}>
                <input
                  type="radio"
                  value={PackageType.PRODUCT}
                  checked={packageType === PackageType.PRODUCT}
                  onChange={() => handlePackageTypeChange(PackageType.PRODUCT)}
                  disabled={isSubmitting}
                  aria-describedby="product-package-description"
                />
                <span className={styles.radioText}>PRODUCT Package</span>
                <span id="product-package-description" className={styles.radioDescription}>
                  Product-specific package with product code
                </span>
              </label>
              <label className={styles.radioLabel}>
                <input
                  type="radio"
                  value={PackageType.DEFAULT}
                  checked={packageType === PackageType.DEFAULT}
                  onChange={() => handlePackageTypeChange(PackageType.DEFAULT)}
                  disabled={isSubmitting}
                  aria-describedby="default-package-description"
                />
                <span className={styles.radioText}>DEFAULT Package</span>
                <span id="default-package-description" className={styles.radioDescription}>
                  Baseline features every firm in the selected jurisdiction receives automatically
                </span>
              </label>
            </div>
          </div>
        </section>

        {/* 1b. Status Section */}
        <section className={styles.formSection}>
          <h3 className={styles.sectionTitle}>Status</h3>
          <div className={styles.formGroup}>
            <div className={styles.toggleContainer}>
              <div className={styles.statusRow}>
                <span id="status-helper-text" className={styles.helperText}>
                  {isActive
                    ? "Package is active and available for use"
                    : "Package is inactive and not available for use"}
                </span>
                <Controller
                  name="isActive"
                  control={control}
                  render={({ field: { value, onChange, onBlur, name, ref } }) => (
                    <label className={styles.toggleLabel}>
                      <input
                        type="checkbox"
                        checked={value}
                        onChange={onChange}
                        onBlur={onBlur}
                        name={name}
                        ref={ref}
                        disabled={true}
                        className={styles.toggleInput}
                        aria-label="Package active status"
                        aria-describedby="status-helper-text"
                      />
                      <span className={styles.toggleSwitch} />
                      <span className={styles.toggleText}>{value ? "Active" : "Inactive"}</span>
                    </label>
                  )}
                />
              </div>
              <div className={styles.infoBox}>
                <Icon icon="info" size="small" className={styles.infoIcon} />
                <p className={styles.infoText}>Only one version can be active at a time</p>
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* 2. Package Details Section */}
      <section className={styles.formSection}>
        <h3 className={styles.sectionTitle}>Package Details</h3>

        {/* DEFAULT Package Fields */}
        {isDefaultPackage && (
          <div className={styles.detailsGrid}>
            <div className={styles.detailsLeftColumn}>
              <div className={styles.formGroup}>
                <Input
                  {...register("name")}
                  label="Package Name"
                  placeholder="Enter package name"
                  error={errors.name?.message}
                  helperText='A descriptive name for this package (e.g., "Basic Legal Research")'
                  fullWidth
                  required
                  disabled={isSubmitting}
                />
              </div>
              <div className={styles.formGroup}>
                <Input
                  {...register("productCode")}
                  label="Package Code"
                  placeholder="e.g. DEFAULT-GLOBAL"
                  error={errors.productCode?.message}
                  helperText="Unique code for this default package (convention: DEFAULT-<SCOPE>)"
                  fullWidth
                  required
                  disabled={isSubmitting || mode === "edit"}
                />
              </div>
            </div>

            <div className={styles.detailsRightColumn}>
              <div className={styles.formGroup}>
                <Textarea
                  {...register("description")}
                  label="Description"
                  placeholder="Enter package description"
                  error={errors.description?.message}
                  helperText="Optional description of what this package includes"
                  fullWidth
                  disabled={isSubmitting}
                />
              </div>
            </div>
          </div>
        )}

        {/* PRODUCT Package Fields */}
        {isProductPackage && (
          <div className={styles.detailsGrid}>
            <div className={styles.detailsLeftColumn}>
              <div className={styles.formGroup}>
                <Input
                  {...register("name")}
                  label="Name"
                  placeholder="Enter package name"
                  error={errors.name?.message}
                  helperText="A descriptive name for this package"
                  fullWidth
                  required
                  disabled={isSubmitting}
                />
              </div>

              <div className={styles.formGroup}>
                <Input
                  {...register("productCode")}
                  label="Product Code"
                  placeholder="Enter product code (e.g., L500-PREMIUM-2024)"
                  error={errors.productCode?.message}
                  helperText={
                    mode === "edit"
                      ? "Product code cannot be changed after creation"
                      : "Unique identifier for this product package"
                  }
                  fullWidth
                  required
                  disabled={isSubmitting || mode === "edit"}
                />
              </div>
            </div>

            <div className={styles.detailsRightColumn}>
              <div className={styles.formGroup}>
                <Textarea
                  {...register("description")}
                  label="Description"
                  placeholder="Enter package description"
                  error={errors.description?.message}
                  helperText="Optional description of what this package includes"
                  fullWidth
                  disabled={isSubmitting}
                />
              </div>
            </div>
          </div>
        )}
      </section>

      {/* 3. Default Region Section */}
      <section className={styles.formSection}>
        <h3 className={styles.sectionTitle}>Default Region</h3>
        <div className={styles.formGroup}>
          <div
            className={styles.radioGroup}
            role="radiogroup"
            aria-label="Default location type selector"
          >
            <label className={styles.radioLabel}>
              <input
                type="radio"
                value="region"
                checked={locationType === "region"}
                onChange={() => {
                  setLocationType("region");
                  setValue("defaultSiteStructureId", null, { shouldDirty: true });
                }}
                disabled={isSubmitting}
              />
              <span className={styles.radioText}>Region</span>
            </label>
            <label className={styles.radioLabel}>
              <input
                type="radio"
                value="country"
                checked={locationType === "country"}
                onChange={() => {
                  setLocationType("country");
                  setValue("defaultSiteStructureId", null, { shouldDirty: true });
                }}
                disabled={isSubmitting}
              />
              <span className={styles.radioText}>Country</span>
            </label>
          </div>

          <Controller
            name="defaultSiteStructureId"
            control={control}
            render={({ field: { value, onChange }, fieldState }) => {
              const regionOptions = includesHistoricalData
                ? withGlobalRegionFirst(locationRegionOptions)
                : locationRegionOptions;
              const sourceOptions =
                locationType === "region" ? regionOptions : locationCountryOptions;
              const currentValue = typeof value === "string" ? value : null;
              const initialOption: AsyncSelectOption | null =
                currentValue !== null
                  ? (sourceOptions.find(o => o.value === currentValue) ?? null)
                  : null;
              return (
                <AsyncSearchableSelect
                  key={locationType}
                  name="defaultSiteStructureId"
                  label={locationType === "region" ? "Default Region" : "Default Country"}
                  required={false}
                  value={currentValue}
                  onSearch={async (query: string) => {
                    const q = query.toLowerCase();
                    return sourceOptions.filter(o => o.label.toLowerCase().includes(q));
                  }}
                  onChange={selectedValue => {
                    onChange(selectedValue !== null ? String(selectedValue) : null);
                  }}
                  disabled={isSubmitting}
                  placeholder={
                    locationType === "region" ? "Search for a region..." : "Search for a country..."
                  }
                  helperText={
                    includesHistoricalData
                      ? `Fallback region for subscriptions when CRM does not provide one. ${HISTORICAL_DATA_GLOBAL_HINT}`
                      : "Fallback region for subscriptions when CRM does not provide one"
                  }
                  error={fieldState.error?.message}
                  debounceMs={0}
                  minSearchLength={0}
                  initialOption={initialOption}
                />
              );
            }}
          />
        </div>
      </section>

      {/* 4. Features & Metadata Grid */}
      <div className={styles.featuresMetadataGrid}>
        {/* 3a. Features Section */}
        <section className={styles.formSection}>
          <h3 className={styles.sectionTitle}>Features</h3>
          <div className={styles.formGroup}>
            <Controller
              name="featureCodes"
              control={control}
              render={({ field }) => (
                <FeaturePicker
                  selectedFeatureCodes={field.value ?? []}
                  onChange={field.onChange}
                  error={errors.featureCodes?.message}
                  disabled={isSubmitting}
                  columns={featurePickerColumns}
                  compact={featurePickerCompact}
                  autoFit={featurePickerAutoFit}
                />
              )}
            />
          </div>
        </section>

        {hasEliteFeatures && (
          <Controller
            name="metadataQuantities"
            control={control}
            render={({ field }) => (
              <MetadataQuantitiesConfig
                featureCodes={featureCodes || []}
                metadata={field.value || null}
                onChange={field.onChange}
                disabled={isSubmitting}
                submitAttemptCount={submitAttemptCount}
                onSaveAnyway={handleSaveAnyway}
                onMissingFieldsChange={setMissingFieldsCount}
              />
            )}
          />
        )}
      </div>

      {/* Actions Section */}
      <div className={styles.formActions}>
        <Button variant="outlined" color="default" onClick={onCancel} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          disabled={isSubmitting}
          className={styles.submitButton}
        >
          {isSubmitting ? "Saving..." : submitLabel}
        </Button>
      </div>
    </form>
  );
}
